"use client";

import React from "react";
import { ShieldCheck, DollarSign, Tractor, Clock, Leaf, Bug, Award, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const reasons = [{
  title: "FDACS Licensed",
  description: "Fully insured and state-certified pest control and termite operators. We meet the highest regulatory standards for your safety and peace of mind.",
  icon: ShieldCheck,
  dark: true,
  colSpan: "col-span-12 md:col-span-6 lg:col-span-4"
}, {
  title: "Plans from $35/mo",
  description: "Affordable, reliable protection without breaking the bank. Transparent pricing with absolutely no hidden fees or surprise charges.",
  icon: DollarSign,
  dark: false,
  colSpan: "col-span-12 md:col-span-6 lg:col-span-4"
}, {
  title: "Arcadia Ranch Experts",
  description: "Specialized knowledge in treating large properties, ranches, and agricultural spaces unique to the Southwest Florida area.",
  icon: Tractor,
  dark: true,
  colSpan: "col-span-12 md:col-span-6 lg:col-span-4"
}, {
  title: "Same-Day Service",
  description: "Pests do not wait, and neither do we. Fast, reliable response times for urgent infestations when you need help the most.",
  icon: Clock,
  dark: false,
  colSpan: "col-span-12 md:col-span-6 lg:col-span-3"
}, {
  title: "Family & Pet Safe",
  description: "Targeted treatments that are ruthlessly tough on bugs but completely safe for your loved ones and furry friends.",
  icon: Leaf,
  dark: true,
  colSpan: "col-span-12 md:col-span-6 lg:col-span-3"
}, {
  title: "Termite Specialists",
  description: "Advanced baiting and liquid treatments to protect your biggest investment from silent, destructive wood-destroying organisms.",
  icon: Bug,
  dark: false,
  colSpan: "col-span-12 md:col-span-6 lg:col-span-3"
}, {
  title: "100% Guarantee",
  description: "If the bugs come back between scheduled visits, so do we—at no extra cost. Your complete satisfaction is our top priority.",
  icon: Award,
  dark: true,
  colSpan: "col-span-12 md:col-span-6 lg:col-span-3"
}];
export default function WhyChoosePeaceRiver() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="whychoosepeaceriver" className="bg-background py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-10" style={{
      backgroundImage: 'repeating-linear-gradient(45deg, var(--color-foreground) 0, var(--color-foreground) 1px, transparent 0, transparent 50%)',
      backgroundSize: '20px 20px'
    }}></div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-16 transition-all duration-700 transform", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground uppercase tracking-tight mb-6">
            Why Choose Peace River?
          </h2>
          <p className="font-serif text-xl text-foreground/80 leading-relaxed">
            We combine industrial-strength solutions with local expertise to deliver unmatched pest control across Punta Gorda, Port Charlotte, and beyond.
          </p>
        </div>

        <div ref={gridRef} className={cn("grid grid-cols-12 gap-6 transition-all duration-1000 transform", isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {reasons.map((item, i) => {
          const Icon = item.icon;
          return <Card key={i} data-index={i} className={cn("rounded-none shadow-none border-4 border-foreground transition-transform hover:-translate-y-2 duration-300", item.dark ? "bg-primary text-primary-foreground" : "bg-card text-card-foreground", item.colSpan)}>
                <CardHeader className="pb-2">
                  <div className={cn("w-16 h-16 flex items-center justify-center mb-4", item.dark ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground")} style={{
                clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)'
              }}>
                    <Icon className="w-8 h-8" />
                  </div>
                  <CardTitle className="font-default text-2xl uppercase tracking-wide">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className={cn("font-serif text-lg leading-relaxed", item.dark ? "text-primary-foreground/90" : "text-card-foreground/80")}>
                    {item.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

        <div className="mt-16 text-center">
          <Button asChild size="lg" className="rounded-none font-default uppercase tracking-wider text-lg h-14 px-8 border-2 border-foreground shadow-[4px_4px_0px_0px_var(--color-foreground)] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all">
            <Link to="/ContactPage">
              Schedule Free Inspection <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}